import { useState } from 'react';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { ObjectArea, ObjectAreaId } from './objectAreas';
import type { AreaHint } from './objectAreaStatus';
import { AreaHintMark, OBJECT_AREA_ICONS, ObjectAreaNav } from './ObjectAreaNav';

interface Props {
  groupId: string;
  areas: ObjectArea[];
  active?: ObjectArea;
  hints: Partial<Record<ObjectAreaId, AreaHint>>;
}

/**
 * Bereichsleiste des Objekts. Ab einer Arbeitsbreite von 56 rem steht sie als Spalte neben dem
 * Inhalt; darunter wird sie zur Disclosure, die den aktuellen Bereich nennt (Muster des
 * Einstellungen-Hubs). Gemessen wird der Container, nicht das Fenster — neben der globalen
 * Navigation lügt ein Viewport-Breakpoint (FRONTEND_GUIDELINES §9).
 */
export function ObjectAreaNavigation({ groupId, areas, active, hints }: Props) {
  const [open, setOpen] = useState(false);
  const ActiveIcon = active ? OBJECT_AREA_ICONS[active.id] : LayoutGrid;

  return (
    <aside className="@4xl:sticky @4xl:top-6 @4xl:self-start">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="objekt-bereiche"
        className="border-line bg-card hover:bg-card-alt/60 focus-visible:ring-sage @4xl:hidden flex min-h-11 w-full items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2"
      >
        <ActiveIcon size={17} className="text-ink-soft shrink-0" aria-hidden />
        <span className="text-ink min-w-0 flex-1 truncate font-serif text-base">
          {active?.label ?? 'Bereiche'}
        </span>
        {active && <AreaHintMark hint={hints[active.id]} active={false} />}
        <ChevronDown
          size={16}
          aria-hidden
          className={cn('text-ink-faint shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      <div
        id="objekt-bereiche"
        className={cn(
          '@4xl:mt-0 @4xl:block @4xl:rounded-none @4xl:border-0 @4xl:bg-transparent @4xl:p-0',
          open ? 'border-line bg-card mt-2 rounded-lg border p-2' : 'hidden',
        )}
      >
        <ObjectAreaNav
          groupId={groupId}
          areas={areas}
          activeId={active?.id}
          hints={hints}
          onNavigate={() => setOpen(false)}
        />
      </div>
    </aside>
  );
}
